// src/components/Auth/LoginForm.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import SEOHead from '../SEO/SEOHead';
import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    InputAdornment,
    IconButton,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    Link,
    Divider,
    useTheme,
    alpha,
    useMediaQuery,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Lock as LockIcon,
    ArrowForward as ArrowForwardIcon,
    Fingerprint as FingerprintIcon,
    Face as FaceIcon,
} from '@mui/icons-material';
import GoogleLoginButton from './GoogleLoginButton';

export const LoginForm = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { login, error: authError, setError, loading, promptBiometricEnroll, biometric, loginWithBiometric } = useAuth();
    const theme = useTheme();
    const themeContext = useThemeContext();
    const themeMode = themeContext?.themeMode || 'light';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Animation states
    const [showForm, setShowForm] = useState(false);

    // Biometric login state
    const [biometricLoading, setBiometricLoading] = useState(false);
    const [biometricAttempted, setBiometricAttempted] = useState(false);
    const biometricCanLogin = biometric?.isNative && biometric?.isEnabled;

    // Form state management
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [showPassword, setShowPassword] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [loginAttempted, setLoginAttempted] = useState(false);

    // Trigger animation on component mount
    useEffect(() => {
        setShowForm(true);
    }, []);

    // Auto-trigger biometric login when the login page loads (if enabled)
    useEffect(() => {
        if (!biometricCanLogin || biometricAttempted || biometric?.checking) return;
        setBiometricAttempted(true);

        const triggerBiometric = async () => {
            try {
                setBiometricLoading(true);
                await loginWithBiometric();
                navigate('/dashboard');
            } catch (err) {
                if (err.biometricInvalidated) {
                    // Fingerprints changed on device — show actionable message
                    setError(err.message);
                }
                // Otherwise user cancelled or failed — password form is already visible
            } finally {
                setBiometricLoading(false);
            }
        };

        // Small delay so the page renders first
        const timer = setTimeout(triggerBiometric, 400);
        return () => clearTimeout(timer);
    }, [biometricCanLogin, biometricAttempted, biometric?.checking, loginWithBiometric, navigate]);

    // Manual biometric login trigger (tap the fingerprint button)
    const handleBiometricLogin = async () => {
        try {
            setBiometricLoading(true);
            setError(null);
            await loginWithBiometric();
            navigate('/dashboard');
        } catch (err) {
            if (err.biometricInvalidated) {
                setError(err.message);
            } else {
                setError(t('auth.biometric.failed', 'Fingerprint login failed. Please use your password.'));
            }
        } finally {
            setBiometricLoading(false);
        }
    };

    // Load remembered email if available
    useEffect(() => {
        const rememberedEmail = localStorage.getItem('rememberedEmail');
        if (rememberedEmail) {
            setFormData(prev => ({
                ...prev,
                email: rememberedEmail,
                rememberMe: true
            }));
        }
    }, []);

    // Form validation
    const validateForm = () => {
        const errors = {};

        if (!formData.email) {
            errors.email = t('validation.email', 'Please enter a valid email address');
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
            errors.email = t('validation.email', 'Please enter a valid email address');
        }

        if (!formData.password) {
            errors.password = t('validation.password', 'Password must be at least 8 characters long');
        } else if (formData.password.length < 6) {
            errors.password = t('validation.password', 'Password must be at least 8 characters long');
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoginAttempted(true);

        if (!validateForm()) {
            return;
        }

        try {
            const result = await login({
                email: formData.email,
                password: formData.password,
                rememberMe: formData.rememberMe
            });

            // Handle email not yet verified
            if (result?.requiresEmailVerification) {
                navigate('/email-pending', { state: { email: result.email } });
                return;
            }

            // Handle "Remember Me" functionality - save email only
            if (formData.rememberMe) {
                localStorage.setItem('rememberedEmail', formData.email);
            } else {
                localStorage.removeItem('rememberedEmail');
            }

            // Handle cross-app redirect (e.g. ?redirect=dms)
            const searchParams = new URLSearchParams(window.location.search);
            const redirectTarget = searchParams.get('redirect');
            if (redirectTarget === 'dms') {
                const token = localStorage.getItem('token');
                try {
                    const handoffRes = await axios.post(
                        `${process.env.REACT_APP_API_URL || ''}/api/auth/dms-handoff`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const code = handoffRes.data?.code;
                    if (code) {
                        window.location.href = `https://dms.soldikeeper.com?code=${code}`;
                        return;
                    }
                } catch (handoffErr) {
                    console.error('DMS handoff failed:', handoffErr);
                }
                window.location.href = 'https://dms.soldikeeper.com';
                return;
            }

            // Prompt biometric enrollment after successful password login
            promptBiometricEnroll(formData.email, formData.password);

            // Navigate to dashboard on successful login
            navigate('/dashboard');
        } catch (err) {
            console.error('Login failed:', err);
        }
    };

    // Handle input changes
    const handleInputChange = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear validation errors when user starts typing
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleSwitchToRegister = () => {
        navigate('/register');
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            position: 'relative',
            overflow: 'hidden',
            background: themeMode === 'dark'
                ? 'linear-gradient(160deg, #0a0f1e 0%, #0d1f2d 60%, #0a1a12 100%)'
                : 'linear-gradient(160deg, #f0fdf4 0%, #ecfdf5 60%, #f0f9ff 100%)',
        }}>
            <SEOHead title="Sign In" noIndex={true} canonical="/login" description="Sign in to your SoldiKeeper account to manage your budget, track expenses, and split bills." />
            <Box sx={{
                position: 'absolute', width: 400, height: 400, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)',
                top: '-10%', right: '-10%', pointerEvents: 'none',
            }} />
            <Box sx={{
                position: 'absolute', width: 300, height: 300, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
                bottom: '5%', left: '-8%', pointerEvents: 'none',
            }} />

            {/* Back to landing */}
            <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
                <Button
                    onClick={() => navigate('/')}
                    size="small"
                    sx={{
                        textTransform: 'none',
                        color: 'text.secondary',
                        fontSize: '0.8rem',
                        '&:hover': { color: '#10b981' },
                    }}
                >
                    ← SoldiKeeper
                </Button>
            </Box>

            {/* Form container */}
            <Box sx={{
                width: '100%',
                maxWidth: 400,
                zIndex: 1,
            }}>
                {/* Logo + title */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box
                        component="img"
                        src="/assets/images/logo.png"
                        alt="SoldiKeeper"
                        onError={(e) => { e.target.style.display = 'none'; }}
                        sx={{ width: 48, height: 48, borderRadius: 2, mb: 1.5 }}
                    />
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                        {t('auth.login.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.login.subtitle')}
                    </Typography>
                </Box>

                {authError && loginAttempted && (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                        {authError}
                    </Alert>
                )}

                {/* Social / biometric buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
                    <GoogleLoginButton variant="outlined" fullWidth size="large" />
                    {biometricCanLogin && (
                        <Button
                            variant="outlined"
                            fullWidth
                            size="large"
                            onClick={handleBiometricLogin}
                            disabled={biometricLoading || loading}
                            startIcon={biometricLoading
                                ? <CircularProgress size={18} color="inherit" />
                                : biometric?.biometryType === 'face' ? <FaceIcon /> : <FingerprintIcon />}
                            sx={{
                                height: 48, borderRadius: 2.5, textTransform: 'none', fontWeight: 600,
                                borderColor: '#10b981', color: '#10b981',
                                '&:hover': { borderColor: '#059669', bgcolor: alpha('#10b981', 0.06) },
                            }}
                        >
                            {biometricLoading
                                ? t('common.loading', 'Verifying...')
                                : biometric?.biometryType === 'face'
                                    ? t('auth.login.faceId', 'Sign in with Face ID')
                                    : t('auth.login.fingerprint', 'Sign in with Fingerprint')}
                        </Button>
                    )}
                </Box>

                {/* Divider */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.disabled" sx={{ mx: 2 }}>
                        {t('auth.login.orEmail', 'or continue with email')}
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                </Box>

                {/* Email/password form */}
                <form onSubmit={handleSubmit} noValidate>
                    <TextField
                        fullWidth
                        label={t('auth.login.email')}
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        error={!!validationErrors.email}
                        helperText={validationErrors.email}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2.5,
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981', borderWidth: 2 },
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        label={t('auth.login.password')}
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleInputChange('password')}
                        error={!!validationErrors.password}
                        helperText={validationErrors.password}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 1.5,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2.5,
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981', borderWidth: 2 },
                            },
                        }}
                    />

                    {/* Remember me + forgot */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.rememberMe}
                                    onChange={handleInputChange('rememberMe')}
                                    size="small"
                                    sx={{ color: '#10b981', '&.Mui-checked': { color: '#10b981' } }}
                                />
                            }
                            label={<Typography variant="body2">{t('auth.login.rememberMe', 'Remember me')}</Typography>}
                        />
                        <Link href="/forgot-password" underline="hover" variant="body2" sx={{ color: '#10b981', fontWeight: 500 }}>
                            {t('auth.login.forgotPassword')}
                        </Link>
                    </Box>

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={loading}
                        endIcon={!loading && <ArrowForwardIcon />}
                        disableElevation
                        sx={{
                            height: 50, borderRadius: 2.5, textTransform: 'none',
                            fontWeight: 700, fontSize: '1rem',
                            bgcolor: '#10b981',
                            '&:hover': { bgcolor: '#059669' },
                            '&:active': { transform: 'scale(0.98)' },
                            transition: 'all 0.2s ease',
                            mb: 3,
                        }}
                    >
                        {loading ? <CircularProgress size={22} color="inherit" /> : t('auth.login.submit')}
                    </Button>

                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        {t('auth.login.noAccount')}{' '}
                        <Box
                            component="span"
                            onClick={handleSwitchToRegister}
                            sx={{ color: '#10b981', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                            {t('auth.login.signUp')}
                        </Box>
                    </Typography>
                </form>
            </Box>
        </Box>
    );
};

export default LoginForm;